import React from 'react';
import { Bell, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext'; // Updated import path

interface NotificationPanelProps {
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose }) => {
  const { currentUser } = useAuth();
  const { notifications, markAsRead, markAllAsRead } = useNotifications();
  const { t } = useLanguage();
  const userNotifications = currentUser ? notifications.filter(n => n.userId === currentUser.id) : [];
  const unreadNotifications = userNotifications.filter(n => !n.isRead);

  return (
    <div className="modal-overlay">
      <div className="modal-content w-full max-w-md max-h-96">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{t('notification.title')}</h2>
          <div className="flex items-center space-x-2">
            {unreadNotifications.length > 0 && (
              <button 
                onClick={() => currentUser && markAllAsRead(currentUser.id)} 
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {t('notification.mark.all.read')}
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {userNotifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>{t('notification.noNotifications')}</p>
            </div>
          ) : (
            userNotifications.map(notification => (
              <div 
                key={notification.id} 
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${notification.isRead ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'}`} 
                onClick={() => markAsRead(notification.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{notification.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                    <p className="text-xs text-gray-400 mt-2">{notification.createdAt && notification.createdAt !== '' ? new Date(notification.createdAt).toLocaleString() : 'No date'}</p>
                  </div>
                  {!notification.isRead && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;
